import React, { useState, useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { ContractClient } from './contract';
import { ReadOnlyContractClient } from './contract-readonly';
import { iso2ToNumeric, iso3ToIso2 } from './countryCodes';
import { fitGeometryToCap } from './services/spatial/geometryBudget';
import { parseVectorFile, firstPolygonGeometry, VECTOR_IMPORT_ACCEPT } from './services/spatial/vectorImport';
import { extractCountryInfo, validateCountryCode, getFlagUrl } from './utils/countryVaultUtils';
import KYCVerificationModal from './components/KYCVerificationModal';
import { AddressInputWithQR } from './components/AddressInputWithQR';
import WebRtcPolicySelector from './components/WebRtcPolicySelector';
import AgentCountryPolicySelector from './components/AgentCountryPolicySelector';
import TeleportExtensionPolicySelector from './components/TeleportExtensionPolicySelector';
import OrganizationsSection from './components/organizations/OrganizationsSection';
import './CountryManagementOverlay.css';

// ─── Stellar Explorer helpers ─────────────────────────────────────────────────
// Priority: REACT_APP_STELLAR_NETWORK → RPC URL parse → default testnet
// Add REACT_APP_STELLAR_NETWORK=testnet to Azure App Service settings.
const _STELLAR_NETWORK: 'testnet' | 'mainnet' = (() => {
  const explicit = process.env.REACT_APP_STELLAR_NETWORK;
  if (explicit === 'mainnet') return 'mainnet';
  if (explicit === 'testnet') return 'testnet';
  const rpc = process.env.REACT_APP_SOROBAN_RPC_URL || '';
  return rpc.includes('testnet') ? 'testnet' : 'testnet'; // default testnet
})();
function _explorerTx(hash?: string | null) {
  if (!hash) return null;
  return `https://stellar.expert/explorer/${_STELLAR_NETWORK}/tx/${hash}`;
}
function _explorerContract(id?: string | null) {
  if (!id) return null;
  return `https://stellar.expert/explorer/${_STELLAR_NETWORK}/contract/${id}`;
}
const _REWARDS_CONTRACT_ID = process.env.REACT_APP_GEOFENCE_REWARDS_ID || null;

type OverlayTab = 'overview' | 'vault' | 'policy' | 'organizations' | 'kyc' | 'borders';

const TAB_LABEL: Record<OverlayTab, string> = {
  overview: 'Overview',
  vault: 'Vault',
  policy: 'Policy',
  organizations: 'Organizations',
  kyc: 'KYC',
  borders: 'Borders',
};

interface CountryManagementOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  countryCode: number;
  countryName: string;
  contractClient: ContractClient | null;
  walletAddress: string | null;
  mainAdminAddress: string | null;
  isMainAdmin: boolean;
  map: mapboxgl.Map | null;
  onPolicyChanged?: () => void;
  onCountryAllowedChange?: (countryCode: number, allowed: boolean) => void;
  /** Called after borders are successfully saved so the main map can refresh */
  onBordersChanged?: () => void;
}

export const CountryManagementOverlay: React.FC<CountryManagementOverlayProps> = ({
  isOpen,
  onClose,
  countryCode,
  countryName,
  contractClient,
  walletAddress,
  mainAdminAddress,
  isMainAdmin,
  map,
  onPolicyChanged,
  onCountryAllowedChange,
  onBordersChanged,
}) => {
  const [activeTab, setActiveTab] = useState<OverlayTab>('overview');
  const [countryAdmin, setCountryAdmin] = useState<string | null>(null);
  const [isLoadingAdmin, setIsLoadingAdmin] = useState(false);
  const [isCountryAdmin, setIsCountryAdmin] = useState(false);
  const [isAllowed, setIsAllowed] = useState(false);
  const [isLoadingPolicy, setIsLoadingPolicy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalGeoJson, setOriginalGeoJson] = useState<any>(null);
  const [currentGeoJson, setCurrentGeoJson] = useState<any>(null);
  const [mapContainer, setMapContainer] = useState<HTMLDivElement | null>(null);
  const [countryMap, setCountryMap] = useState<mapboxgl.Map | null>(null);
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const drawRef = useRef<MapboxDraw | null>(null);
  const countryFeatureRef = useRef<any>(null);
  const mapRemovedRef = useRef<boolean>(false);
  const borderVectorInputRef = useRef<HTMLInputElement | null>(null);
  
  // Country Vault state
  const [vaultInfo, setVaultInfo] = useState<any>(null);
  const [isLoadingVault, setIsLoadingVault] = useState(false);
  const [isRegisteringVault, setIsRegisteringVault] = useState(false);
  const [isTogglingVault, setIsTogglingVault] = useState(false);
  const [countryIso2, setCountryIso2] = useState<string | null>(null);
  
  // Transfer policy state
  const [transferPolicies, setTransferPolicies] = useState<Map<string, boolean>>(new Map());
  const [isLoadingTransferPolicies, setIsLoadingTransferPolicies] = useState(false);
  const [allCountries, setAllCountries] = useState<Array<{ iso2: string; name: string }>>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);
  
  // Country Policy (CountryPolicyV2) state
  const [isLoadingCompliance, setIsLoadingCompliance] = useState(false);
  const [isSavingCompliance, setIsSavingCompliance] = useState(false);
  const [complianceError, setComplianceError] = useState<string | null>(null);
  const [policyVersion, setPolicyVersion] = useState(1);
  // true = policy was loaded from chain; false = no on-chain policy yet (showing defaults)
  const [policyExistsOnChain, setPolicyExistsOnChain] = useState(false);

  // Policy toggles
  const [depositsEnabledInput, setDepositsEnabledInput] = useState(false);
  const [withdrawalsEnabledInput, setWithdrawalsEnabledInput] = useState(false);
  const [transfersInEnabledInput, setTransfersInEnabledInput] = useState(false);
  const [transfersOutEnabledInput, setTransfersOutEnabledInput] = useState(false);
  const [kycRequiredInput, setKycRequiredInput] = useState(false);
  const [sanctionsScreenedInput, setSanctionsScreenedInput] = useState(false);
  const [restrictedInput, setRestrictedInput] = useState(false);
  const [requireWebauthnWithdrawInput, setRequireWebauthnWithdrawInput] = useState(false);
  const [requireZkForSessionJoinInput, setRequireZkForSessionJoinInput] = useState(false);
  const [allowPublicSessionsInput, setAllowPublicSessionsInput] = useState(false);

  // Policy numeric inputs (XLM, stored as stroops internally)
  const [minTxInput, setMinTxInput] = useState('');
  const [maxTxInput, setMaxTxInput] = useState('');
  const [dailyLimitInput, setDailyLimitInput] = useState('');
  const [monthlyLimitInput, setMonthlyLimitInput] = useState('');
  const [feeBpsInput, setFeeBpsInput] = useState('');
  const [countryAdminShareBpsInput, setCountryAdminShareBpsInput] = useState('');
  
  // KYC Management state
  const [kycSearchAddress, setKycSearchAddress] = useState('');
  const [kycSearchResult, setKycSearchResult] = useState<any>(null);
  const [isLoadingKYCSearch, setIsLoadingKYCSearch] = useState(false);
  const [showKYCVerificationModal, setShowKYCVerificationModal] = useState(false);

  // Proposal workflow state (Phase 4)
  const [lastProposal, setLastProposal] = useState<any>(null);
  const [isSubmittingProposal, setIsSubmittingProposal] = useState(false);
  const [proposalError, setProposalError] = useState<string | null>(null);
  const [isApprovingProposal, setIsApprovingProposal] = useState(false);
  const [isRejectingProposal, setIsRejectingProposal] = useState(false);
  // Diff metrics fetched from /api/admin/geofence/proposals/:id/diff (Phase 7)
  const [proposalDiff, setProposalDiff] = useState<any>(null);

  // On-chain receipt / escrow state (GeoTrustGeofenceRewards contract)
  const [onChainReceipt, setOnChainReceipt] = useState<any>(null);
  const [onChainEscrow, setOnChainEscrow] = useState<any>(null);
  const [onChainAvailable, setOnChainAvailable] = useState<boolean | null>(null);
  const [isAnchoringOnChain, setIsAnchoringOnChain] = useState(false);
  const [isFundingReward, setIsFundingReward] = useState(false);
  const [isReleasingReward, setIsReleasingReward] = useState(false);
  const [isRefundingReward, setIsRefundingReward] = useState(false);
  const [fundTokenAddr, setFundTokenAddr] = useState('');
  const [fundAmountStr, setFundAmountStr] = useState('');

  // Inline notification (replaces all alert() calls)
  const [notification, setNotification] = useState<{ type: 'success' | 'error' | 'warning'; message: string } | null>(null);
  const notifTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showNotification = (type: 'success' | 'error' | 'warning', message: string) => {
    if (notifTimerRef.current) clearTimeout(notifTimerRef.current);
    setNotification({ type, message });
    notifTimerRef.current = setTimeout(() => setNotification(null), 5000);
  };

  // Fetch diff metrics from the server whenever a new proposal is set (Phase 7)
  useEffect(() => {
    if (!lastProposal?.id) {
      setProposalDiff(null);
      return;
    }
    let cancelled = false;
    const diffWallet = walletAddress ? `?walletAddress=${encodeURIComponent(walletAddress)}` : '';
    fetch(`/api/admin/geofence/proposals/${lastProposal.id}/diff${diffWallet}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (!cancelled && data?.diff) setProposalDiff(data.diff); })
      .catch(() => {/* diff is optional — silently swallow */});
    return () => { cancelled = true; };
  }, [lastProposal?.id, walletAddress]);

  // Refresh on-chain receipt + escrow whenever proposal enters a final state
  useEffect(() => {
    const finalStatuses = ['approved', 'rewardFunded', 'paid'];
    if (!lastProposal?.id || !finalStatuses.includes(lastProposal?.status)) {
      setOnChainReceipt(null);
      setOnChainEscrow(null);
      return;
    }
    let cancelled = false;
    const wa = walletAddress ? `?walletAddress=${encodeURIComponent(walletAddress)}` : '';
    fetch(`/api/admin/geofence/proposals/${lastProposal.id}/onchain${wa}`)
      .then(async r => {
        if (r.status === 503) { if (!cancelled) setOnChainAvailable(false); return null; }
        if (!r.ok) return null;
        return r.json();
      })
      .then(data => {
        if (cancelled || !data) return;
        setOnChainAvailable(true);
        setOnChainReceipt(data.receipt ?? null);
        setOnChainEscrow(data.escrow ?? null);
      })
      .catch(() => {/* on-chain query is optional */});
    return () => { cancelled = true; };
  }, [lastProposal?.id, lastProposal?.status, walletAddress]);

  // Check if current user is country admin
  useEffect(() => {
    const checkCountryAdmin = async () => {
      if (!contractClient || !walletAddress) {
        setIsCountryAdmin(false);
        setIsLoadingAdmin(false);
        return;
      }
      
      setIsLoadingAdmin(true);
      try {
        // Use read-only client to avoid Freighter prompts
        const readOnlyClient = new ReadOnlyContractClient();
        const admin = await readOnlyClient.getCountryAdmin(countryCode);
        setCountryAdmin(admin);
        setIsCountryAdmin(admin !== null && admin === walletAddress);
      } catch (error) {
        // console.error('[CountryManagement] Failed to load country admin:', error);
        setIsCountryAdmin(false);
      } finally {
        setIsLoadingAdmin(false);
      }
    };

    if (isOpen) {
      checkCountryAdmin();
    }
  }, [isOpen, countryCode, contractClient, walletAddress]);

  // Load country policy
  useEffect(() => {
    const loadPolicy = async () => {
      setIsLoadingPolicy(true);
      try {
        // Use read-only client to avoid Freighter prompts
        const readOnlyClient = new ReadOnlyContractClient();
        const allowed = await readOnlyClient.getCountryAllowed(countryCode);
        setIsAllowed(allowed ?? false);
      } catch (error) {
        // console.error('[CountryManagement] Failed to load policy:', error);
      } finally {
        setIsLoadingPolicy(false);
      }
    };

    if (isOpen) {
      loadPolicy();
    }
  }, [isOpen, countryCode, contractClient]);

  // Load country GeoJSON and extract ISO2 code
  useEffect(() => {
    const loadCountryGeoJson = async () => {
      // console.log('[CountryManagement] Loading GeoJSON for country:', countryCode);
      try {
        // Use the API endpoint (not the static file) so we always read from the
        // same physical file that /api/countries/update writes to.  The static
        // file (build/countries.geojson) can be a stale pre-build snapshot when
        // running the production build, whereas /api/countries always reads the
        // live public/countries.geojson (or build/ in production).
        const response = await fetch('/api/countries', {
          headers: { 'Cache-Control': 'no-cache' },
        });
        if (!response.ok) throw new Error('Failed to load countries.geojson');
        
        const geojson = await response.json();
        // console.log('[CountryManagement] Loaded GeoJSON, searching for country:', countryCode);
        const countryFeature = geojson.features.find((f: any) => {
          // Try ISO_NUMERIC first
          let code = f.properties?.ISO_NUMERIC;
          if (code) {
            return Number(code) === countryCode;
          }
          
          // If not found, try converting from ISO2 or ISO3 (like the main map does)
          const iso3 = f.id || f.properties?.id;
          const iso2 = f.properties?.ISO2;
          
          if (iso2) {
            code = iso2ToNumeric(iso2);
            if (code === countryCode) return true;
          }
          
          if (iso3) {
            const iso3Str = typeof iso3 === 'string' ? iso3 : String(iso3);
            const iso2FromIso3 = iso3ToIso2(iso3Str);
            if (iso2FromIso3) {
              code = iso2ToNumeric(iso2FromIso3);
              if (code === countryCode) return true;
            }
          }
          
          return false;
        });

        if (countryFeature) {
          // console.log('[CountryManagement] Found country feature:', countryFeature.properties?.name);
          const featureCopy = JSON.parse(JSON.stringify(countryFeature)); // Deep copy
          setOriginalGeoJson(featureCopy);
          setCurrentGeoJson(featureCopy);
          countryFeatureRef.current = featureCopy;
          
          // Extract ISO2 code for vault operations
          const countryInfo = extractCountryInfo(countryFeature);
          if (countryInfo && countryInfo.iso2) {
            setCountryIso2(countryInfo.iso2);
          } else {
            // Fallback: try to get from properties
            const iso2 = countryFeature.properties?.ISO2 || countryFeature.properties?.iso_a2;
            if (iso2 && validateCountryCode(iso2)) {
              setCountryIso2(iso2.toUpperCase());
            } else {
              const iso3 = countryFeature.id || countryFeature.properties?.id;
              if (iso3) {
                const iso2FromIso3 = iso3ToIso2(String(iso3));
                if (iso2FromIso3 && validateCountryCode(iso2FromIso3)) {
                  setCountryIso2(iso2FromIso3.toUpperCase());
                }
              }
            }
          }
        } else {
          // console.warn(`[CountryManagement] Country ${countryCode} not found in GeoJSON. Available codes:`, 
          //   geojson.features.slice(0, 5).map((f: any) => ({ 
          //     code: f.properties?.ISO_NUMERIC, 
          //     name: f.properties?.name || f.properties?.NAME 
          //   }))
          // );
          // Set a flag that country wasn't found, but still allow map to initialize
          // The map will show without borders, but user can still draw new borders
          setOriginalGeoJson(null);
          setCurrentGeoJson(null);
          countryFeatureRef.current = null;
          setCountryIso2(null);
        }
      } catch (error) {
        // console.error('[CountryManagement] Failed to load GeoJSON:', error);
        setCountryIso2(null);
      }
    };

    if (isOpen) {
      loadCountryGeoJson();
    } else {
      // Reset when closed
      countryFeatureRef.current = null;
      setOriginalGeoJson(undefined); // Reset to undefined to indicate not loaded
      setCurrentGeoJson(null);
      setCountryIso2(null);
      setVaultInfo(null);
      // Clean up map when overlay closes
      if (countryMap && !mapRemovedRef.current) {
        try {
          const container = countryMap.getContainer();
          if (container && container.parentNode) {
            countryMap.remove();
            mapRemovedRef.current = true;
          }
        } catch (error) {
          // console.warn('[CountryManagement] Error removing map on close:', error);
        }
        setCountryMap(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, countryCode]);
  
  // Load vault info when ISO2 code is available
  useEffect(() => {
    const loadVaultInfo = async () => {
      if (!countryIso2 || !contractClient || !isOpen) {
        setVaultInfo(null);
        return;
      }
      
      setIsLoadingVault(true);
      try {
        // Use read-only client to avoid wallet prompts
        const readOnlyClient = new ReadOnlyContractClient();
        const info = await readOnlyClient.getCountryInfo(countryIso2);
        setVaultInfo(info);
      } catch (error) {
        // console.error('[CountryManagement] Failed to load vault info:', error);
        setVaultInfo(null);
      } finally {
        setIsLoadingVault(false);
      }
    };
    
    loadVaultInfo();
  }, [countryIso2, contractClient, isOpen]);

  // Load all countries and transfer policies
  useEffect(() => {
    const loadCountriesAndPolicies = async () => {
      if (!isOpen || !countryIso2) {
        setAllCountries([]);
        setTransferPolicies(new Map());
        return;
      }
      
      setIsLoadingCountries(true);
      setIsLoadingTransferPolicies(true);
      
      try {
        // Use read-only client to avoid wallet prompts
        const readOnlyClient = new ReadOnlyContractClient();
        
        // Load all countries from GeoJSON
        const response = await fetch('/countries.geojson');
        if (response.ok) {
          const geojson = await response.json();
          if (geojson.features) {
            
            // Try multiple property name variations (like AdminPanel does)
            const countries = geojson.features
              .map((feature: any) => {
                // Try different property name variations
                const iso2 = feature.properties?.ISO2 || feature.properties?.iso_a2 || feature.properties?.ISO_A2;
                const name = feature.properties?.name || feature.properties?.NAME || feature.properties?.NAME_EN || feature.properties?.name_en;
                
                // If no ISO2 in properties, try to get from ISO3 conversion
                let finalIso2 = iso2;
                if (!finalIso2) {
                  const iso3 = feature.id || feature.properties?.id || feature.properties?.ISO3 || feature.properties?.iso_a3;
                  if (iso3) {
                    const iso3Str = typeof iso3 === 'string' ? iso3 : String(iso3);
                    finalIso2 = iso3ToIso2(iso3Str);
                  }
                }
                
                if (finalIso2 && name) {
                  return { iso2: String(finalIso2).toUpperCase(), name: String(name) };
                }
                return null;
              })
              .filter((c: any) => c !== null)
              .sort((a: any, b: any) => a.name.localeCompare(b.name));
            
            // console.log('[CountryManagement] Extracted', countries.length, 'countries from GeoJSON');
            
            setAllCountries(countries);
            setIsLoadingCountries(false);

            // Load transfer policies in parallel (one RPC call per country, batched)
            const others = countries.filter((c: any) => c.iso2 !== countryIso2.toUpperCase());
            const results = await Promise.all(
              others.map(async (country: any) => {
                try {
                  const allowed = await readOnlyClient.getTransferAllowed(
                    countryIso2.toUpperCase(),
                    country.iso2
                  );
                  return [country.iso2, allowed] as [string, boolean];
                } catch {
                  return [country.iso2, true] as [string, boolean];
                }
              })
            );
            const policies = new Map<string, boolean>(results);
            setTransferPolicies(policies);
          } else {
            setAllCountries([]);
          }
        } else {
          setAllCountries([]);
        }
      } catch (error) {
        setAllCountries([]);
      } finally {
        setIsLoadingCountries(false);
        setIsLoadingTransferPolicies(false);
      }
    };
    
    loadCountriesAndPolicies();
  }, [isOpen, countryIso2]);

  // Load full CountryPolicyV2
  useEffect(() => {
    const loadPolicy = async () => {
      if (!(isMainAdmin || isCountryAdmin) || !isOpen || !countryCode) return;
      setIsLoadingCompliance(true);
      setComplianceError(null);
      setPolicyExistsOnChain(false);
      try {
        const readOnlyClient = new ReadOnlyContractClient();
        const p = await readOnlyClient.getFullCountryPolicy(countryCode);
        setPolicyExistsOnChain(!!p);
        if (p) {
          setDepositsEnabledInput(p.deposits_enabled);
          setWithdrawalsEnabledInput(p.withdrawals_enabled);
          setTransfersInEnabledInput(p.transfers_in_enabled);
          setTransfersOutEnabledInput(p.transfers_out_enabled);
          setKycRequiredInput(p.kyc_required);
          setSanctionsScreenedInput(p.sanctions_screened);
          setRestrictedInput(p.restricted);
          setRequireWebauthnWithdrawInput(p.require_webauthn_withdraw);
          setRequireZkForSessionJoinInput(p.require_zk_for_session_join);
          setAllowPublicSessionsInput(p.allow_public_sessions);
          setMinTxInput(p.min_tx_amount > 0 ? (p.min_tx_amount / 10_000_000).toFixed(7) : '');
          setMaxTxInput(p.max_tx_amount > 0 ? (p.max_tx_amount / 10_000_000).toFixed(7) : '');
          setDailyLimitInput(p.daily_limit > 0 ? (p.daily_limit / 10_000_000).toFixed(7) : '');
          setMonthlyLimitInput(p.monthly_limit > 0 ? (p.monthly_limit / 10_000_000).toFixed(7) : '');
          // Display as plain percentages (divide bps by 100): 1000 bps → "10"
          setFeeBpsInput(p.fee_bps > 0 ? String(parseFloat((p.fee_bps / 100).toFixed(4))) : '');
          setCountryAdminShareBpsInput(p.country_admin_share_bps > 0 ? String(parseFloat((p.country_admin_share_bps / 100).toFixed(4))) : '');
          setPolicyVersion(p.policy_version);
        } else {
          // No policy saved yet — pre-fill the form with open defaults so the admin
          // sees an "everything enabled" starting point instead of all-blocked.
          setDepositsEnabledInput(true);
          setWithdrawalsEnabledInput(true);
          setTransfersInEnabledInput(true);
          setTransfersOutEnabledInput(true);
          setKycRequiredInput(false);
          setSanctionsScreenedInput(false);
          setRestrictedInput(false);
          setRequireWebauthnWithdrawInput(false);
          setRequireZkForSessionJoinInput(false);
          setAllowPublicSessionsInput(true);
          setMinTxInput('');
          setMaxTxInput('');
          setDailyLimitInput('');
          setMonthlyLimitInput('');
          setFeeBpsInput('');
          setCountryAdminShareBpsInput('');
          setPolicyVersion(1);
        }
      } catch (error: any) {
        setComplianceError(error.message || 'Failed to load country policy');
      } finally {
        setIsLoadingCompliance(false);
      }
    };
    loadPolicy();
  }, [isMainAdmin, isCountryAdmin, isOpen, countryCode]);

  // Handle KYC search
  const handleKYCSearch = async () => {
    if (!kycSearchAddress || !countryIso2) return;
    
    setIsLoadingKYCSearch(true);
    setKycSearchResult(null);
    
    try {
      const readOnlyClient = new ReadOnlyContractClient();
      const kycData = await readOnlyClient.getKYCData(kycSearchAddress, countryIso2);
      setKycSearchResult(kycData);
    } catch (error: any) {
      setKycSearchResult(null);
    } finally {
      setIsLoadingKYCSearch(false);
    }
  };

  // Handle vault registration
  const handleRegisterVault = async () => {
    if (!contractClient) { showNotification('error', 'Connect your wallet to perform this action'); return; }
    if (!countryIso2 || !walletAddress || !validateCountryCode(countryIso2)) {
      showNotification('error', 'Invalid country code or wallet address');
      return;
    }
    
    try {
      setIsRegisteringVault(true);
      await contractClient.registerCountry(countryIso2, countryName, walletAddress);
      showNotification('success', `Successfully registered ${countryIso2} (${countryName}) for country vault`);
      const readOnlyClient = new ReadOnlyContractClient();
      const info = await readOnlyClient.getCountryInfo(countryIso2);
      setVaultInfo(info);
    } catch (error: any) {
      const isAlreadyRegistered =
        error.message?.includes('already registered') ||
        error.message?.includes('Contract, #4') ||
        error.message?.includes('CountryAlreadyRegistered');
      if (isAlreadyRegistered) {
        showNotification('warning', `Country ${countryIso2} is already registered`);
        const readOnlyClient = new ReadOnlyContractClient();
        const info = await readOnlyClient.getCountryInfo(countryIso2);
        setVaultInfo(info);
      } else {
        showNotification('error', `Failed to register country vault: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setIsRegisteringVault(false);
    }
  };
  
  // Handle vault enable/disable
  const handleToggleVault = async () => {
    if (!contractClient) { showNotification('error', 'Connect your wallet to perform this action'); return; }
    if (!countryIso2 || !walletAddress || !vaultInfo) {
      return;
    }

    const enabling = !vaultInfo.enabled;

    try {
      setIsTogglingVault(true);
      await contractClient.setCountryEnabled(countryIso2, enabling, walletAddress);

      // When enabling, auto-set a default open compliance policy so deposits/withdrawals work immediately
      if (enabling) {
        const numericCode = iso2ToNumeric(countryIso2.toUpperCase());
        if (numericCode) {
          try {
            await contractClient.setCountryPolicy(numericCode, {
              depositsEnabled: true,
              withdrawalsEnabled: true,
              transfersInEnabled: true,
              transfersOutEnabled: true,
              kycRequired: false,
              sanctionsScreened: false,
              restricted: false,
              requireWebauthnWithdraw: false,
              requireZkForSessionJoin: false,
              allowPublicSessions: true,
              minTxAmount: 0,
              maxTxAmount: 0,
              dailyLimit: 0,
              monthlyLimit: 0,
              feeBps: 0,
              countryAdminShareBps: 0,
              policyVersion: 1,
            });
          } catch (policyErr: any) {
            // Non-fatal: vault is enabled, policy just wasn't set
          }
        }
      }

      showNotification('success', `Country vault ${enabling ? 'enabled' : 'disabled'} for ${countryIso2}`);
      const readOnlyClient = new ReadOnlyContractClient();
      const info = await readOnlyClient.getCountryInfo(countryIso2);
      setVaultInfo(info);
    } catch (error: any) {
      showNotification('error', `Failed to toggle country vault: ${error.message || 'Unknown error'}`);
    } finally {
      setIsTogglingVault(false);
    }
  };

  // Initialize map for country editing
  useEffect(() => {
    // console.log('[CountryManagement] Map init effect:', { 
    //   mapContainer: !!mapContainer, 
    //   isOpen, 
    //   hasFeature: !!countryFeatureRef.current,
    //   hasOriginalGeoJson: !!originalGeoJson 
    // });
    
    if (!mapContainer || !isOpen) {
      // console.log('[CountryManagement] Map init skipped: no container or not open');
      return;
    }
    
    // Wait for GeoJSON to load (originalGeoJson will be set to null if not found)
    if (originalGeoJson === undefined) {
      // Still loading - wait
      // console.log('[CountryManagement] Waiting for country GeoJSON to load...');
      return;
    }
    
    // If we get here, either we have a feature (originalGeoJson is an object) 
    // or we've confirmed it doesn't exist (originalGeoJson is null)
    // Initialize the map either way

    // Clean up existing map if it exists
    if (countryMap && !mapRemovedRef.current) {
      // console.log('[CountryManagement] Cleaning up existing map');
      try {
        // Check if map is still valid before removing
        const container = countryMap.getContainer();
        if (container && container.parentNode) {
          countryMap.remove();
          mapRemovedRef.current = true;
        }
      } catch (error) {
        // console.warn('[CountryManagement] Error removing existing map:', error);
      }
      setCountryMap(null);
    }

    // console.log('[CountryManagement] Initializing map with container:', mapContainer);
    const countryMapInstance = new mapboxgl.Map({
      container: mapContainer,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [0, 0],
      zoom: 2,
    });

    countryMapInstance.on('load', () => {
      // console.log('[CountryManagement] Map loaded, adding country feature');
      // Add country feature to map
      const featureToUse = countryFeatureRef.current || originalGeoJson;
      if (featureToUse) {
        countryMapInstance.addSource('country', {
          type: 'geojson',
          data: featureToUse,
        });

        countryMapInstance.addLayer({
          id: 'country-fill',
          type: 'fill',
          source: 'country',
          paint: {
            'fill-color': '#088',
            'fill-opacity': 0.3,
          },
        });

        countryMapInstance.addLayer({
          id: 'country-outline',
          type: 'line',
          source: 'country',
          paint: {
            'line-color': '#088',
            'line-width': 2,
          },
        });

        // Fit map to country bounds
        const bounds = new mapboxgl.LngLatBounds();
        const coordinates = featureToUse.geometry.coordinates;
        
        const processCoordinates = (coords: any) => {
          if (Array.isArray(coords[0]) && typeof coords[0][0] === 'number') {
            coords.forEach((coord: [number, number]) => {
              bounds.extend(coord);
            });
          } else {
            coords.forEach((subCoords: any) => {
              processCoordinates(subCoords);
            });
          }
        };

        if (featureToUse.geometry.type === 'Polygon') {
          processCoordinates(coordinates);
        } else if (featureToUse.geometry.type === 'MultiPolygon') {
          coordinates.forEach((polygon: any) => {
            processCoordinates(polygon);
          });
        }

        countryMapInstance.fitBounds(bounds, { padding: 50 });
      } else {
        // Country not found in GeoJSON - center on a default location
        // console.log('[CountryManagement] Country not found, using default center');
        countryMapInstance.setCenter([0, 0]);
        countryMapInstance.setZoom(2);
      }

      // Navigation + fullscreen controls
      countryMapInstance.addControl(new mapboxgl.NavigationControl({ visualizePitch: false }), 'top-right');

      // Initialize Mapbox Draw for editing
      const draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          polygon: true,
          trash: true,
        },
        defaultMode: 'simple_select',
      });

      countryMapInstance.addControl(draw);
      drawRef.current = draw;

      // Load existing country geometry into draw (if it exists)
      // Reuse featureToUse from above (already declared in this scope)
      if (featureToUse) {
        // Create a feature for draw (draw expects features with id)
        const drawFeature = {
          ...featureToUse,
          id: 'country-feature',
        };
        draw.add(drawFeature);
      }
      // If no feature exists, user can draw a new border from scratch

      // Merge all draw features into one geometry.
      // When a user adds an island (new Polygon) to an existing country feature,
      // draw holds multiple separate features.  We must combine them all into a
      // MultiPolygon so every ring is included in the saved geometry.
      const mergeDrawFeatures = (drawFeatures: any[]) => {
        if (drawFeatures.length === 0) return null;
        if (drawFeatures.length === 1) return drawFeatures[0];

        // Collect all polygon ring-arrays from every feature
        const allPolygons: any[] = [];
        for (const f of drawFeatures) {
          if (f.geometry.type === 'Polygon') {
            // Polygon.coordinates = [outerRing, ...holes]  → one polygon
            allPolygons.push(f.geometry.coordinates);
          } else if (f.geometry.type === 'MultiPolygon') {
            // MultiPolygon.coordinates = [[rings], [rings], ...]  → spread each
            allPolygons.push(...f.geometry.coordinates);
          }
        }

        return {
          ...drawFeatures[0],
          geometry: {
            type: 'MultiPolygon',
            coordinates: allPolygons,
          },
        };
      };

      // Track changes — always merge ALL features so new islands are included
      countryMapInstance.on('draw.update', () => {
        setHasChanges(true);
        const merged = mergeDrawFeatures(draw.getAll().features);
        setCurrentGeoJson(merged);
      });

      countryMapInstance.on('draw.delete', () => {
        setHasChanges(true);
        // Some features may still remain after a delete (e.g. deleted only one island)
        const merged = mergeDrawFeatures(draw.getAll().features);
        setCurrentGeoJson(merged);
      });

      countryMapInstance.on('draw.create', () => {
        setHasChanges(true);
        const merged = mergeDrawFeatures(draw.getAll().features);
        setCurrentGeoJson(merged);
      });
    });

    setCountryMap(countryMapInstance);
    mapRemovedRef.current = false; // Reset flag when creating new map

    return () => {
      // Only clean up if the map hasn't been removed yet
      if (countryMapInstance && !mapRemovedRef.current) {
        try {
          // Check if map is still valid before removing
          const container = countryMapInstance.getContainer();
          if (container && container.parentNode) {
            // Only remove if map is loaded, otherwise just clear the container
            if (countryMapInstance.loaded()) {
              countryMapInstance.remove();
            } else {
              // Map not loaded yet, just clear the container
              container.innerHTML = '';
            }
            mapRemovedRef.current = true;
          }
        } catch (error) {
          // console.warn('[CountryManagement] Error removing map in cleanup:', error);
          mapRemovedRef.current = true; // Mark as removed even on error to prevent retry
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapContainer, isOpen, originalGeoJson]);

  // Check permissions
  const canEdit = isMainAdmin || isCountryAdmin;
  const canEditBorders = isMainAdmin || isCountryAdmin; // Main admin or country admin can edit borders

  /**
   * Which tabs this wallet can see.
   *
   * Each tab's own sections keep their existing permission checks — this
   * only decides whether the tab is worth offering, so nobody clicks
   * through to an empty pane. The two must agree, which is why they are
   * written from the same conditions.
   */
  const visibleTabs: OverlayTab[] = [
    'overview',
    ...(canEdit ? ['vault' as const, 'policy' as const] : []),
    'organizations',
    ...(isMainAdmin && countryIso2 ? ['kyc' as const] : []),
    'borders',
  ];

  // Admin status arrives asynchronously, so a tab can disappear from under
  // a selection made before it resolved.
  useEffect(() => {
    if (!visibleTabs.includes(activeTab)) setActiveTab('overview');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, canEdit, isMainAdmin, countryIso2]);

  const selectTab = (tab: OverlayTab) => {
    setActiveTab(tab);
    // Mapbox measures its container on creation. The borders pane is in
    // the DOM from the start but hidden, so the map is born 0x0 and needs
    // telling once it is actually on screen.
    if (tab === 'borders') setTimeout(() => countryMap?.resize(), 80);
  };

  const handleTogglePolicy = async () => {
    if (!contractClient) { showNotification('error', 'Connect your wallet to perform this action'); return; }
    if (!canEdit) return;
    
    setIsLoadingPolicy(true);
    try {
      const newAllowed = !isAllowed;
      await contractClient.setCountryAllowed(countryCode, newAllowed);
      setIsAllowed(newAllowed);
      showNotification('success', 'Country access policy updated');
      // Notify parent so its allowedCountries state + map re-color immediately
      if (onCountryAllowedChange) onCountryAllowedChange(countryCode, newAllowed);
      if (onPolicyChanged) await onPolicyChanged();
    } catch (error) {
      // console.error('[CountryManagement] Failed to update policy:', error);
      showNotification('error', 'Failed to update country access policy');
    } finally {
      setIsLoadingPolicy(false);
    }
  };

  // ── Geometry helpers ──────────────────────────────────────────────────────────
  const countVertices = (geometry: any): number => {
    if (!geometry || !geometry.coordinates) return 0;
    const flatCoords = (coords: any): number => {
      if (!Array.isArray(coords)) return 0;
      if (typeof coords[0] === 'number') return 1;
      return coords.reduce((sum: number, c: any) => sum + flatCoords(c), 0);
    };
    return flatCoords(geometry.coordinates);
  };

  // ── Import border geometry from a vector file ─────────────────────────────
  // Loads a GeoJSON / KML / GPX / zipped Shapefile, takes its first
  // Polygon/MultiPolygon, replaces the draw layer with it, and marks the
  // editor dirty so the operator can review then submit a proposal.
  const handleImportBorderVector = async (file: File) => {
    setProposalError(null);
    setSaveError(null);
    try {
      const { featureCollection } = await parseVectorFile(file);
      const geometry = firstPolygonGeometry(featureCollection);
      if (!geometry) {
        showNotification('error', 'No Polygon/MultiPolygon geometry found in the file.');
        return;
      }
      const feature: any = {
        type: 'Feature',
        id: 'country-feature',
        geometry,
        properties: countryFeatureRef.current?.properties ?? {},
      };
      const draw = drawRef.current;
      if (draw) {
        draw.deleteAll();
        draw.add(feature);
      }
      setCurrentGeoJson(feature);
      setHasChanges(true);

      // Fly the editor map to the imported geometry's first coordinate.
      try {
        const flat: number[] = [];
        const walk = (c: unknown): void => {
          if (Array.isArray(c)) {
            if (typeof c[0] === 'number' && typeof c[1] === 'number') flat.push(c[0], c[1]);
            else c.forEach(walk);
          }
        };
        walk((geometry as any).coordinates);
        if (flat.length >= 2 && countryMap) countryMap.flyTo({ center: [flat[0], flat[1]], zoom: 5 });
      } catch { /* non-fatal */ }

      showNotification('success', `Imported ${geometry.type} border from ${file.name}. Review and submit for approval.`);
    } catch (error: any) {
      showNotification('error', `Import failed: ${error.message || 'could not read file'}`);
    }
  };

  // ── Submit border proposal (replaces direct save) ─────────────────────────
  const handleSubmitProposal = async () => {
    if (!canEditBorders || !currentGeoJson || !hasChanges) return;

    setIsSubmittingProposal(true);
    setProposalError(null);
    setSaveError(null);

    try {
      // Fetch the live geojson to capture the current state as originalFeatureSnapshot
      const response = await fetch('/api/countries', { headers: { 'Cache-Control': 'no-cache' } });
      if (!response.ok) throw new Error('Failed to read countries.geojson');
      const geojson = await response.json();

      // Locate this country's feature (same lookup logic as the rest of the component)
      const featureIndex = geojson.features.findIndex((f: any) => {
        let code = f.properties?.ISO_NUMERIC;
        if (code) return Number(code) === countryCode;
        const iso3 = f.id || f.properties?.id;
        const iso2 = f.properties?.ISO2;
        if (iso2) { code = iso2ToNumeric(iso2); if (code === countryCode) return true; }
        if (iso3) {
          const iso3Str = typeof iso3 === 'string' ? iso3 : String(iso3);
          const iso2FromIso3 = iso3ToIso2(iso3Str);
          if (iso2FromIso3) { code = iso2ToNumeric(iso2FromIso3); if (code === countryCode) return true; }
        }
        return false;
      });

      // originalFeatureSnapshot: null if country not yet in geojson (new border)
      const originalFeatureSnapshot = featureIndex !== -1
        ? JSON.parse(JSON.stringify(geojson.features[featureIndex]))
        : null;

      // proposedFeatureSnapshot: the currently drawn geometry merged into the feature
      const proposedFeatureSnapshot = originalFeatureSnapshot
        ? { ...originalFeatureSnapshot, geometry: currentGeoJson.geometry }
        : { type: 'Feature', geometry: currentGeoJson.geometry, properties: {} };

      // Enforce the 512 KB feature cap before submitting: round to 6 dp and,
      // if a dense (often imported) coastline still overflows, apply
      // escalating Douglas–Peucker simplification so the proposal isn't
      // rejected outright. Mutates proposedFeatureSnapshot.geometry in place.
      const geomType = proposedFeatureSnapshot.geometry?.type;
      if (geomType === 'Polygon' || geomType === 'MultiPolygon') {
        const fit = fitGeometryToCap(
          proposedFeatureSnapshot as any,
          proposedFeatureSnapshot.geometry as any,
        );
        proposedFeatureSnapshot.geometry = fit.geometry as any;
        if (fit.overCap) {
          throw new Error(
            `Geometry is still ${Math.round(fit.bytes / 1024)} KB after maximum simplification ` +
            `(cap is 512 KB). Reduce the number of vertices or split the territory.`,
          );
        }
        if (fit.simplified) {
          showNotification(
            'warning',
            `Border simplified to fit the 512 KB limit (tolerance ${fit.tolerance}°, ${Math.round(fit.bytes / 1024)} KB).`,
          );
        }
      }

      // Use ISO-3 id if available, otherwise numeric countryCode
      const proposalCountryCode =
        (originalFeatureSnapshot?.id && typeof originalFeatureSnapshot.id === 'string')
          ? originalFeatureSnapshot.id
          : countryCode;

      const submitResponse = await fetch('/api/geofence/proposals', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // X-Wallet-Address lets the x402 admin bypass work even before the
          // body is inspected (belt-and-suspenders alongside body.walletAddress)
          ...(walletAddress ? { 'X-Wallet-Address': walletAddress } : {}),
        },
        body: JSON.stringify({
          countryCode:             proposalCountryCode,
          originalFeatureSnapshot,
          proposedFeatureSnapshot,
          walletAddress:           walletAddress ?? undefined,  // for x402 admin bypass
          submittedBy:             walletAddress ?? undefined,
          metadata: { countryName, numericCode: countryCode },
        }),
      });

      if (!submitResponse.ok) {
        const errorData = await submitResponse.json().catch(() => ({ error: 'Failed to submit proposal' }));
        throw new Error(errorData.error || errorData.message || `Submit failed: ${submitResponse.status}`);
      }

      const { proposal } = await submitResponse.json();
      setLastProposal(proposal);
      setHasChanges(false);
      showNotification('success', `Border proposal submitted (ID: ${proposal.id.slice(0, 8)}…). Awaiting admin approval.`);
    } catch (error: any) {
      const msg = error.message || 'Failed to submit proposal';
      setProposalError(msg);
      if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) {
        showNotification('error', `Failed to connect to backend. Is the server running? (${msg})`);
      } else {
        showNotification('error', `Failed to submit proposal: ${msg}`);
      }
    } finally {
      setIsSubmittingProposal(false);
    }
  };

  // ── Approve proposal (admin only) ─────────────────────────────────────────
  // After approval the editor mini-map has two stale layers:
  //   1. country-fill/country-outline — still showing the PRE-PROPOSAL geometry
  //   2. Mapbox Draw — still holding the PROPOSED (now approved) geometry
  // We replace the GeoJSON source with the server-confirmed approved feature,
  // clear draw, and re-add the approved shape so only one version is visible.
  const syncEditorMapToApproved = (approvedFeature: any) => {
    try {
      const src = countryMap?.getSource('country') as mapboxgl.GeoJSONSource | undefined;
      if (src) src.setData(approvedFeature);
    } catch { /* map may have been removed */ }

    try {
      drawRef.current?.deleteAll();
      if (approvedFeature) {
        drawRef.current?.add({ ...approvedFeature, id: 'country-feature' });
      }
    } catch { /* draw may not be initialised */ }

    // Keep the component ref in sync so any subsequent re-open uses approved geometry
    countryFeatureRef.current = approvedFeature;
  };

  const handleApproveProposal = async (proposalId: string) => {
    if (!proposalId) return;
    setIsApprovingProposal(true);
    try {
      const res = await fetch(`/api/admin/geofence/proposals/${proposalId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ walletAddress: walletAddress ?? undefined, force: false }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 409 && data.hint) {
          // Snapshot conflict — offer force override
          const proceed = window.confirm(
            `Snapshot conflict:\n${data.message}\n\nClick OK to force-apply the proposal anyway.`
          );
          if (proceed) {
            const forceRes = await fetch(`/api/admin/geofence/proposals/${proposalId}/approve`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ walletAddress: walletAddress ?? undefined, force: true }),
            });
            const forceData = await forceRes.json();
            if (!forceRes.ok) throw new Error(forceData.error || 'Force approve failed');
            setLastProposal(forceData.proposal);
            showNotification('success', 'Proposal approved (forced). Border is now live.');
            if (onBordersChanged) onBordersChanged();
            // Sync the editor mini-map to the approved geometry so neither
            // the stale country-fill nor the old draw layer remain visible.
            if (forceData.updatedFeature) {
              syncEditorMapToApproved(forceData.updatedFeature);
            }
          }
          return;
        }
        throw new Error(data.error || `Approve failed: ${res.status}`);
      }
      setLastProposal(data.proposal);
      showNotification('success', 'Proposal approved. Border is now live.');
      if (onBordersChanged) onBordersChanged();
      // Sync the editor mini-map to the approved geometry so neither
      // the stale country-fill nor the old draw layer remain visible.
      if (data.updatedFeature) {
        syncEditorMapToApproved(data.updatedFeature);
      }
    } catch (error: any) {
      showNotification('error', `Approve failed: ${error.message}`);
    } finally {
      setIsApprovingProposal(false);
    }
  };

  // ── Reject proposal (admin only) ─────────────────────────────────────────
  const handleRejectProposal = async (proposalId: string) => {
    if (!proposalId) return;
    const reason = window.prompt('Enter rejection reason (optional):') ?? '';
    setIsRejectingProposal(true);
    try {
      const res = await fetch(`/api/admin/geofence/proposals/${proposalId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ walletAddress: walletAddress ?? undefined, reason }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Reject failed: ${res.status}`);
      setLastProposal(data.proposal);
      showNotification('warning', 'Proposal rejected.');
    } catch (error: any) {
      showNotification('error', `Reject failed: ${error.message}`);
    } finally {
      setIsRejectingProposal(false);
    }
  };

  // ── On-chain reward handlers ──────────────────────────────────────────────

  const handleAnchorOnChain = async () => {
    if (!lastProposal?.id) return;
    setIsAnchoringOnChain(true);
    try {
      const res = await fetch(`/api/admin/geofence/proposals/${lastProposal.id}/anchor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ walletAddress: walletAddress ?? undefined }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Anchor failed');
      setLastProposal(data.proposal);
      setOnChainReceipt(null); // trigger re-fetch
      showNotification('success', `Anchored on-chain. tx: ${data.submitHash?.slice(0, 10)}…`);
      // Re-fetch on-chain status
      const waQ = walletAddress ? `?walletAddress=${encodeURIComponent(walletAddress)}` : '';
      const onchainRes = await fetch(`/api/admin/geofence/proposals/${lastProposal.id}/onchain${waQ}`);
      if (onchainRes.ok) {
        const od = await onchainRes.json();
        if (od.success) { setOnChainReceipt(od.receipt ?? null); setOnChainEscrow(od.escrow ?? null); }
      }
    } catch (err: any) {
      showNotification('error', err.message);
    } finally {
      setIsAnchoringOnChain(false);
    }
  };

  const handleFundReward = async () => {
    if (!lastProposal?.id || !fundTokenAddr.trim() || !fundAmountStr.trim()) return;
    const amount = Number(fundAmountStr.trim());
    if (isNaN(amount) || amount <= 0) {
      showNotification('error', 'Enter a valid positive amount (in token base units)');
      return;
    }
    setIsFundingReward(true);
    try {
      const res = await fetch(`/api/admin/geofence/proposals/${lastProposal.id}/fund-onchain`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tokenAddr: fundTokenAddr.trim(), amount, walletAddress: walletAddress ?? undefined }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Fund failed');
      setLastProposal(data.proposal);
      setFundTokenAddr('');
      setFundAmountStr('');
      showNotification('success', `Escrow funded. tx: ${data.hash?.slice(0, 10)}…`);
      const waQ2 = walletAddress ? `?walletAddress=${encodeURIComponent(walletAddress)}` : '';
      const od = await (await fetch(`/api/admin/geofence/proposals/${lastProposal.id}/onchain${waQ2}`)).json();
      if (od.success) { setOnChainReceipt(od.receipt ?? null); setOnChainEscrow(od.escrow ?? null); }
    } catch (err: any) {
      showNotification('error', err.message);
    } finally {
      setIsFundingReward(false);
    }
  };

  const handleReleaseReward = async () => {
    if (!lastProposal?.id) return;
    setIsReleasingReward(true);
    try {
      const res = await fetch(`/api/admin/geofence/proposals/${lastProposal.id}/release-onchain`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ walletAddress: walletAddress ?? undefined }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Release failed');
      setLastProposal(data.proposal);
      showNotification('success', `Reward released to agent. tx: ${data.hash?.slice(0, 10)}…`);
      const waQ3 = walletAddress ? `?walletAddress=${encodeURIComponent(walletAddress)}` : '';
      const od = await (await fetch(`/api/admin/geofence/proposals/${lastProposal.id}/onchain${waQ3}`)).json();
      if (od.success) { setOnChainReceipt(od.receipt ?? null); setOnChainEscrow(od.escrow ?? null); }
    } catch (err: any) {
      showNotification('error', err.message);
    } finally {
      setIsReleasingReward(false);
    }
  };

  const handleRefundReward = async () => {
    if (!lastProposal?.id) return;
    if (!window.confirm('Refund the escrowed reward back to the treasury? This cannot be undone.')) return;
    setIsRefundingReward(true);
    try {
      const res = await fetch(`/api/admin/geofence/proposals/${lastProposal.id}/refund-onchain`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ walletAddress: walletAddress ?? undefined }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Refund failed');
      setLastProposal(data.proposal);
      showNotification('success', `Escrow refunded to treasury. tx: ${data.hash?.slice(0, 10)}…`);
      const waQ4 = walletAddress ? `?walletAddress=${encodeURIComponent(walletAddress)}` : '';
      const od = await (await fetch(`/api/admin/geofence/proposals/${lastProposal.id}/onchain${waQ4}`)).json();
      if (od.success) { setOnChainReceipt(od.receipt ?? null); setOnChainEscrow(od.escrow ?? null); }
    } catch (err: any) {
      showNotification('error', err.message);
    } finally {
      setIsRefundingReward(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="cmo-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="cmo-panel" onClick={(e) => e.stopPropagation()}>
        {/*
          Header, notifications and the tab strip sit outside the scrolling
          area. The panel used to be one long scroller, which carried the
          title and the close button off the top of the screen and left
          notifications firing where nobody could see them.
        */}
        <div className="cmo-header">
          <div className="cmo-title-row">
            <h2 className="cmo-title">
              {countryIso2 && (
                <img
                  src={getFlagUrl(countryIso2)}
                  alt={countryIso2}
                  style={{ width: '28px', height: 'auto', borderRadius: '3px', flexShrink: 0 }}
                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              )}
              <span className="cmo-title-text">Manage {countryName}</span>
              <span className="cmo-title-code">Code {countryCode}</span>
            </h2>
            <button onClick={onClose} className="cmo-close" aria-label="Close">×</button>
          </div>

          {/* Inline notification bar */}
          {notification && (
            <div style={{
              padding: '10px 16px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: notification.type === 'success' ? '#d4edda' : notification.type === 'error' ? '#f8d7da' : '#fff3cd',
              color: notification.type === 'success' ? '#155724' : notification.type === 'error' ? '#721c24' : '#856404',
              border: `1px solid ${notification.type === 'success' ? '#c3e6cb' : notification.type === 'error' ? '#f5c6cb' : '#ffeeba'}`,
            }}>
              <span>{notification.message}</span>
              <button onClick={() => setNotification(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', opacity: 0.6, marginLeft: '12px', padding: 0 }}>×</button>
            </div>
          )}

          <div className="cmo-tabs" role="tablist">
            {visibleTabs.map((tab) => (
              <button
                key={tab}
                role="tab"
                aria-selected={activeTab === tab}
                className={`cmo-tab${activeTab === tab ? ' is-active' : ''}`}
                onClick={() => selectTab(tab)}
              >
                {TAB_LABEL[tab]}
              </button>
            ))}
          </div>
        </div>

        {/*
          Panes stay mounted and are hidden rather than unmounted. Each one
          holds loaded contract state and, in the case of Borders, a live
          Mapbox instance; tearing those down and rebuilding them on every
          tab click would turn a free interaction into an expensive one.
        */}
        <div className="cmo-pane" hidden={activeTab !== 'overview'}>
        {/* Admin Status */}
        <div style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
          {isLoadingAdmin ? (
            <div>Loading admin status...</div>
          ) : (
            <div>
              <div>
                <strong>Your Role:</strong>{' '}
                {isMainAdmin ? 'Main Admin' : isCountryAdmin ? 'Country Admin' : 'No Admin Rights'}
              </div>
              {countryAdmin && (
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  Country Admin: {countryAdmin.substring(0, 8)}...{countryAdmin.substring(48)}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Policy Settings */}
        <div style={{ padding: '12px', border: '1px solid #ddd', borderRadius: '4px', marginBottom: '12px' }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>Policy Settings</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span>Country Status:</span>
            <button
              className={`country-toggle ${isAllowed ? 'allowed' : 'denied'}`}
              onClick={handleTogglePolicy}
              disabled={!canEdit || isLoadingPolicy}
            >
              {isAllowed ? 'Allowed' : 'Denied'}
            </button>
            {!canEdit && (
              <span style={{ fontSize: '12px', color: '#666' }}>
                (Only admins can change this)
              </span>
            )}
          </div>
        </div>

        </div>

        <div className="cmo-pane" hidden={activeTab !== 'vault'}>
        {/* Country Vault Settings */}
        {(isMainAdmin || isCountryAdmin) && (
          <div style={{ padding: '12px', border: '1px solid #ddd', borderRadius: '4px', marginBottom: '12px', backgroundColor: '#f9f9f9' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>Country Vault</h3>
            {!countryIso2 ? (
              <div style={{ fontSize: '12px', color: '#666' }}>
                ISO2 code not available for this country
              </div>
            ) : (
              <>
                {isLoadingVault ? (
                  <div style={{ fontSize: '12px', color: '#666' }}>Loading vault status...</div>
                ) : vaultInfo ? (
                  <div>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Vault Status:</strong>{' '}
                      <span style={{
                        color: vaultInfo.enabled ? '#28a745' : '#dc3545',
                        fontWeight: 'bold'
                      }}>
                        {vaultInfo.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                      <div>ISO Code: <strong>{vaultInfo.alpha2
                        ? (typeof vaultInfo.alpha2 === 'string'
                            ? vaultInfo.alpha2
                            : new TextDecoder().decode(vaultInfo.alpha2 instanceof Uint8Array ? vaultInfo.alpha2 : new Uint8Array(vaultInfo.alpha2)))
                        : (vaultInfo.numeric_code || countryIso2)}</strong></div>
                      <div>Registered: {vaultInfo.created_at ? new Date(vaultInfo.created_at * 1000).toLocaleString() : 'N/A'}</div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        className="primary-button"
                        onClick={handleToggleVault}
                        disabled={isTogglingVault}
                        style={{
                          fontSize: '12px',
                          padding: '6px 12px',
                          backgroundColor: vaultInfo.enabled ? '#dc3545' : '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        {isTogglingVault ? 'Processing...' : (vaultInfo.enabled ? 'Disable Vault' : 'Enable Vault')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                      Country vault not registered for <strong>{countryIso2}</strong>
                    </div>
                    <button
                      className="primary-button"
                      onClick={handleRegisterVault}
                      disabled={isRegisteringVault}
                      style={{
                        fontSize: '12px',
                        padding: '6px 12px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      {isRegisteringVault ? 'Registering...' : 'Register Country Vault'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Fee configuration is part of Country Policy — see the Compliance Policy section below */}

        {/* Transfer Policy Management (Admin only) */}
        {isMainAdmin && countryIso2 && (
          <div style={{ 
            padding: '16px', 
            border: '1px solid #ddd', 
            borderRadius: '8px', 
            marginBottom: '12px',
            backgroundColor: '#f9f9f9'
          }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: 'bold' }}>
              Transfer Policy Management
            </h3>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
              Control which countries allow transfers from {countryIso2.toUpperCase()}.
              Users can only transfer to countries that are explicitly allowed.
            </div>
            
            {isLoadingTransferPolicies ? (
              <div style={{ padding: '12px', textAlign: 'center' }}>Loading transfer policies...</div>
            ) : (
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {allCountries
                  .filter((country) => country.iso2 !== countryIso2.toUpperCase())
                  .map((country) => {
                    const isAllowed = transferPolicies.get(country.iso2) ?? true;
                    return (
                      <div
                        key={country.iso2}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '8px',
                          marginBottom: '4px',
                          backgroundColor: isAllowed ? '#e8f5e9' : '#ffebee',
                          borderRadius: '4px',
                          border: `1px solid ${isAllowed ? '#4caf50' : '#f44336'}`
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                          <img
                            src={getFlagUrl(country.iso2)}
                            alt={country.iso2}
                            style={{ width: '18px', height: 'auto', borderRadius: '2px', flexShrink: 0 }}
                            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                          <strong>{country.name}</strong>
                          <span style={{ color: '#888', fontSize: '11px' }}>({country.iso2})</span>
                        </div>
                        <button
                          onClick={async () => {
                            if (!contractClient || !countryIso2) return;
                            
                            // Ensure country.iso2 is a string and uppercase (define outside try for error handling)
                            const toCountryCode = String(country.iso2 || '').toUpperCase().trim();
                            const fromCountryCode = String(countryIso2 || '').toUpperCase().trim();
                            
                            try {
                              // Validate country codes are 2 characters
                              if (toCountryCode.length !== 2 || fromCountryCode.length !== 2) {
                                showNotification('error', `Invalid country code: from=${fromCountryCode}, to=${toCountryCode}`);
                                return;
                              }

                              // set_transfer_allowed on the compliance contract does not require
                              // either country to be registered in the core vault registry — it
                              // stores a boolean for any country pair.  Skip the registration
                              // pre-check so admins can deny transfers to countries like Iran that
                              // are not (and should not be) active vault countries.
                              const newAllowed = !isAllowed;
                              await contractClient.setTransferAllowed(
                                fromCountryCode,
                                toCountryCode,
                                newAllowed
                              );

                              const newPolicies = new Map(transferPolicies);
                              newPolicies.set(toCountryCode, newAllowed);
                              setTransferPolicies(newPolicies);

                              showNotification('success', `Transfer from ${fromCountryCode} to ${toCountryCode} ${newAllowed ? 'allowed' : 'denied'}`);
                            } catch (error: any) {
                              showNotification('error', `Failed to update transfer policy: ${error.message || 'Unknown error'}`);
                            }
                          }}
                          style={{
                            padding: '4px 12px',
                            fontSize: '12px',
                            backgroundColor: isAllowed ? '#f44336' : '#4caf50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          {isAllowed ? 'Deny' : 'Allow'}
                        </button>
                      </div>
                    );
                  })}
                {allCountries.filter((c) => c.iso2 !== countryIso2?.toUpperCase()).length === 0 && !isLoadingTransferPolicies && (
                  <div style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                    {isLoadingCountries ? 'Loading countries...' : 'No other countries available. Make sure countries.geojson is loaded.'}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        </div>

        <div className="cmo-pane" hidden={activeTab !== 'policy'}>
        {/* Country Policy (CountryPolicyV2) — Admin only */}
        {(isMainAdmin || isCountryAdmin) && (
          <div style={{
            padding: '16px',
            border: '1px solid #2a3a4a',
            borderRadius: '8px',
            marginBottom: '12px',
            backgroundColor: '#111c26'
          }}>
            <h3 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: 700, color: '#e2e8f0' }}>
              Country Policy
            </h3>
            <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '14px' }}>
              Full <code>CountryPolicyV2</code> for {countryName} — controls deposits, withdrawals, KYC, limits, fees, and sessions.
              {policyVersion > 0 && <span style={{ marginLeft: 8, color: '#4a9eff' }}>v{policyVersion}</span>}
            </div>

            {isLoadingCompliance ? (
              <div style={{ padding: '10px', textAlign: 'center', color: '#888', fontSize: '12px' }}>Loading policy...</div>
            ) : (
              <>
                {/* Warn admin when no policy is saved on-chain yet */}
                {!policyExistsOnChain && !complianceError && (
                  <div style={{ padding: '8px 12px', backgroundColor: '#2a1f00', border: '1px solid #ca8a04', borderRadius: '6px', marginBottom: '12px', fontSize: '12px', color: '#fbbf24' }}>
                    ⚠️ No policy is saved on-chain yet for {countryName}. Deposits, withdrawals, and transfers will be <strong>blocked</strong> until you save this policy. Review the settings below and click <strong>Save Country Policy</strong>.
                  </div>
                )}
                {complianceError && (
                  <div style={{ padding: '8px', backgroundColor: '#2d1a1a', color: '#f87171', borderRadius: '4px', marginBottom: '12px', fontSize: '12px' }}>
                    {complianceError}
                  </div>
                )}

                {/* --- Transaction Permissions --- */}
                <div style={{ marginBottom: '14px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                    Transaction Permissions
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                    {([
                      ['Deposits Enabled', depositsEnabledInput, setDepositsEnabledInput],
                      ['Withdrawals Enabled', withdrawalsEnabledInput, setWithdrawalsEnabledInput],
                      ['Transfers In', transfersInEnabledInput, setTransfersInEnabledInput],
                      ['Transfers Out', transfersOutEnabledInput, setTransfersOutEnabledInput],
                    ] as [string, boolean, (v: boolean) => void][]).map(([label, value, setter]) => (
                      <label key={label} style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '12px', color: '#cbd5e1', cursor: 'pointer' }}>
                        <input type="checkbox" checked={value} onChange={e => setter(e.target.checked)}
                          style={{ width: '14px', height: '14px', accentColor: '#4a9eff', cursor: 'pointer' }} />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>

                {/* --- Compliance Flags --- */}
                <div style={{ marginBottom: '14px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                    Compliance
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                    {([
                      ['KYC Required', kycRequiredInput, setKycRequiredInput],
                      ['Sanctions Screened', sanctionsScreenedInput, setSanctionsScreenedInput],
                      ['Restricted (no txns)', restrictedInput, setRestrictedInput],
                      ['Require WebAuthn Withdraw', requireWebauthnWithdrawInput, setRequireWebauthnWithdrawInput],
                      ['Require ZK for Session Join', requireZkForSessionJoinInput, setRequireZkForSessionJoinInput],
                      ['Allow Public Sessions', allowPublicSessionsInput, setAllowPublicSessionsInput],
                    ] as [string, boolean, (v: boolean) => void][]).map(([label, value, setter]) => (
                      <label key={label} style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '12px', color: '#cbd5e1', cursor: 'pointer' }}>
                        <input type="checkbox" checked={value} onChange={e => setter(e.target.checked)}
                          style={{ width: '14px', height: '14px', accentColor: '#4a9eff', cursor: 'pointer' }} />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>

                {/* --- Transaction Limits --- */}
                <div style={{ marginBottom: '14px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                    Transaction Limits (XLM — leave blank for none)
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    {([
                      ['Min per Tx', minTxInput, setMinTxInput, 'No minimum'],
                      ['Max per Tx', maxTxInput, setMaxTxInput, 'Unlimited'],
                      ['Daily Limit', dailyLimitInput, setDailyLimitInput, 'Unlimited'],
                      ['Monthly Limit', monthlyLimitInput, setMonthlyLimitInput, 'Unlimited'],
                    ] as [string, string, (v: string) => void, string][]).map(([label, value, setter, placeholder]) => (
                      <div key={label}>
                        <label style={{ display: 'block', fontSize: '11px', color: '#94a3b8', marginBottom: '3px' }}>{label}</label>
                        <input type="number" step="0.0000001" min="0" value={value}
                          onChange={e => setter(e.target.value)} placeholder={placeholder}
                          style={{ width: '100%', padding: '5px 7px', fontSize: '12px', backgroundColor: '#0d1721', border: '1px solid #2a3a4a', borderRadius: '4px', color: '#e2e8f0', boxSizing: 'border-box' }} />
                      </div>
                    ))}
                  </div>
                </div>

                {/* --- Fees --- */}
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                    Fees (enter as percentage, e.g. 1 = 1%)
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    {([
                      ['Protocol Fee (%)', feeBpsInput, setFeeBpsInput, '0'],
                      ['Country Admin Share (%)', countryAdminShareBpsInput, setCountryAdminShareBpsInput, '0'],
                    ] as [string, string, (v: string) => void, string][]).map(([label, value, setter, placeholder]) => (
                      <div key={label}>
                        <label style={{ display: 'block', fontSize: '11px', color: '#94a3b8', marginBottom: '3px' }}>{label}</label>
                        <input type="number" step="0.01" min="0" max="100" value={value}
                          onChange={e => setter(e.target.value)} placeholder={placeholder}
                          style={{ width: '100%', padding: '5px 7px', fontSize: '12px', backgroundColor: '#0d1721', border: '1px solid #2a3a4a', borderRadius: '4px', color: '#e2e8f0', boxSizing: 'border-box' }} />
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  onClick={async () => {
                    if (!contractClient || !countryCode) return;
                    setIsSavingCompliance(true);
                    setComplianceError(null);
                    try {
                      const toStroops = (v: string) => v ? Math.floor(parseFloat(v) * 10_000_000) : 0;
                      await contractClient.setCountryPolicy(countryCode, {
                        depositsEnabled: depositsEnabledInput,
                        withdrawalsEnabled: withdrawalsEnabledInput,
                        transfersInEnabled: transfersInEnabledInput,
                        transfersOutEnabled: transfersOutEnabledInput,
                        kycRequired: kycRequiredInput,
                        sanctionsScreened: sanctionsScreenedInput,
                        restricted: restrictedInput,
                        requireWebauthnWithdraw: requireWebauthnWithdrawInput,
                        requireZkForSessionJoin: requireZkForSessionJoinInput,
                        allowPublicSessions: allowPublicSessionsInput,
                        minTxAmount: toStroops(minTxInput),
                        maxTxAmount: toStroops(maxTxInput),
                        dailyLimit: toStroops(dailyLimitInput),
                        monthlyLimit: toStroops(monthlyLimitInput),
                        // Inputs are in plain % (e.g. 10 = 10%); multiply by 100 to get bps
                        feeBps: feeBpsInput ? Math.round(parseFloat(feeBpsInput) * 100) : 0,
                        countryAdminShareBps: countryAdminShareBpsInput ? Math.round(parseFloat(countryAdminShareBpsInput) * 100) : 0,
                        policyVersion: policyVersion + 1,
                      });
                      setPolicyVersion(v => v + 1);
                      setPolicyExistsOnChain(true);
                      showNotification('success', 'Country policy saved successfully!');
                      if (onPolicyChanged) onPolicyChanged();
                    } catch (error: any) {
                      setComplianceError(error.message || 'Failed to save country policy');
                    } finally {
                      setIsSavingCompliance(false);
                    }
                  }}
                  disabled={isSavingCompliance}
                  style={{
                    width: '100%',
                    padding: '9px 16px',
                    backgroundColor: isSavingCompliance ? '#1e293b' : '#1d4ed8',
                    color: isSavingCompliance ? '#64748b' : '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: isSavingCompliance ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    fontWeight: 600,
                    transition: 'background 0.15s',
                  }}
                >
                  {isSavingCompliance ? 'Saving Policy...' : 'Save Country Policy'}
                </button>

                {/* --- WebRTC live-session policy (paid) --- */}
                <div style={{ marginTop: 16 }}>
                  <WebRtcPolicySelector
                    country={countryCode}
                    canEdit={Boolean(isMainAdmin || walletAddress)}
                    walletAddress={walletAddress || undefined}
                    onChange={() => {
                      if (onPolicyChanged) onPolicyChanged();
                    }}
                  />
                </div>

                {/* --- AI-agent presence policy --- */}
                {/* Sibling of the WebRTC policy: same admin gate, same
                    backend-mirror model, additive feature so existing
                    countries keep working as "default-allowed" until an
                    admin explicitly disallows. */}
                <div style={{ marginTop: 16 }}>
                  <AgentCountryPolicySelector
                    country={countryCode}
                    canEdit={Boolean(isMainAdmin || walletAddress)}
                    walletAddress={walletAddress || undefined}
                    onChange={() => {
                      if (onPolicyChanged) onPolicyChanged();
                    }}
                  />
                </div>
                {/* Free teleport-extension policy. Sits next to the
                    agent presence policy because both are AI-agent-
                    economic decisions for the same country admin to
                    make in one sitting. Defaults are set up so a
                    fresh deploy "just works" without anyone having
                    to touch this control — admins only need it when
                    they want to opt OUT for a specific country. */}
                <div style={{ marginTop: 16 }}>
                  <TeleportExtensionPolicySelector
                    country={countryCode}
                    canEdit={Boolean(isMainAdmin || walletAddress)}
                    walletAddress={walletAddress || undefined}
                    onChange={() => {
                      if (onPolicyChanged) onPolicyChanged();
                    }}
                  />
                </div>
              </>
            )}
          </div>
        )}

        </div>

        <div className="cmo-pane" hidden={activeTab !== 'organizations'}>
        {/* Organizations — the tier between this country and its spatial
            objects. Registration is country-scoped on chain, so the
            country admin manages it here rather than in the global panel. */}
        <OrganizationsSection
          countryCode={countryCode}
          countryName={countryName}
          signer={contractClient?.getWallet() ?? null}
          walletAddress={walletAddress}
          networkPassphrase={contractClient?.getNetwork()}
          canAdminister={canEdit}
        />

        </div>

        <div className="cmo-pane" hidden={activeTab !== 'kyc'}>
        {/* KYC Management (Admin only) */}
        {isMainAdmin && countryIso2 && (
          <div style={{ 
            padding: '16px', 
            border: '1px solid #ddd', 
            borderRadius: '8px', 
            marginBottom: '12px',
            backgroundColor: '#f9f9f9'
          }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: 'bold' }}>
              KYC Management
            </h3>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
              View and verify KYC submissions for users in {countryIso2.toUpperCase()}.
            </div>
            
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <AddressInputWithQR
                value={kycSearchAddress}
                onChange={setKycSearchAddress}
                placeholder="Enter wallet address to search"
                inputStyle={{ 
                  flex: 1, 
                  padding: '6px', 
                  fontSize: '12px', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px' 
                }}
                showQRButton={true}
              />
              <button
                onClick={handleKYCSearch}
                disabled={isLoadingKYCSearch || !kycSearchAddress}
                style={{
                  padding: '6px 16px',
                  backgroundColor: isLoadingKYCSearch || !kycSearchAddress ? '#ccc' : '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isLoadingKYCSearch || !kycSearchAddress ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  fontWeight: '500'
                }}
              >
                {isLoadingKYCSearch ? 'Searching...' : 'Search'}
              </button>
            </div>
            
            {kycSearchResult && (
              <div style={{ 
                marginTop: '12px', 
                padding: '12px', 
                backgroundColor: '#fff', 
                borderRadius: '4px',
                border: '1px solid #ddd'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: 'bold' }}>Wallet Address:</div>
                    <div style={{ fontSize: '11px', color: '#666', fontFamily: 'monospace' }}>
                      {kycSearchAddress.slice(0, 8)}...{kycSearchAddress.slice(-8)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: 'bold' }}>Status:</div>
                    <div style={{ 
                      fontSize: '11px', 
                      fontWeight: 'bold',
                      color: kycSearchResult.kycStatus === 'approved' ? '#28a745' : 
                             kycSearchResult.kycStatus === 'pending' ? '#ffc107' : 
                             kycSearchResult.kycStatus === 'rejected' ? '#dc3545' : '#666'
                    }}>
                      {kycSearchResult.kycStatus === 'approved' ? '✓ Approved' : 
                       kycSearchResult.kycStatus === 'pending' ? '⏳ Pending' : 
                       kycSearchResult.kycStatus === 'rejected' ? '✗ Rejected' : 'Not Submitted'}
                    </div>
                  </div>
                </div>
                
                {kycSearchResult.kycStatus && (
                  <div style={{ marginTop: '8px' }}>
                    <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>
                      {kycSearchResult.givenName && kycSearchResult.familyName && (
                        <div><strong>Name:</strong> {kycSearchResult.givenName} {kycSearchResult.familyName}</div>
                      )}
                      {kycSearchResult.emailAddress && (
                        <div><strong>Email:</strong> {kycSearchResult.emailAddress}</div>
                      )}
                      {kycSearchResult.kycVerifiedAt && (
                        <div><strong>Verified At:</strong> {new Date(Number(kycSearchResult.kycVerifiedAt) * 1000).toLocaleString()}</div>
                      )}
                      {kycSearchResult.kycVerifiedBy && (
                        <div><strong>Verified By:</strong> {kycSearchResult.kycVerifiedBy.slice(0, 8)}...{kycSearchResult.kycVerifiedBy.slice(-8)}</div>
                      )}
                    </div>
                    
                    <button
                      onClick={() => setShowKYCVerificationModal(true)}
                      style={{
                        marginTop: '8px',
                        padding: '6px 12px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      {kycSearchResult.kycStatus === 'pending' ? 'Review & Verify' : 'View Details'}
                    </button>
                  </div>
                )}
              </div>
            )}
            
            {kycSearchResult === null && kycSearchAddress && !isLoadingKYCSearch && (
              <div style={{ 
                marginTop: '12px', 
                padding: '8px', 
                backgroundColor: '#fff3cd', 
                borderRadius: '4px',
                fontSize: '12px',
                color: '#856404'
              }}>
                No KYC data found for this wallet address in {countryIso2.toUpperCase()}.
              </div>
            )}
          </div>
        )}

        </div>

        <div className="cmo-pane" hidden={activeTab !== 'borders'}>
        {/* Map Editor — only for main admin / country admin */}
        {canEditBorders && (
          <div style={isMapFullscreen ? {
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            width: '100vw', height: '100vh',
            // Above the backdrop, which also sits at 10000. Equal z-index
            // makes the winner depend on DOM order, and the fullscreen map
            // is a descendant of the backdrop, so it happened to work —
            // until anything reordered. Say what is meant instead.
            zIndex: 10001,
            background: '#fff',
            display: 'flex', flexDirection: 'column',
            padding: '12px', boxSizing: 'border-box',
          } : {
            flex: '0 1 auto', maxHeight: '500px', minHeight: '400px',
            display: 'flex', flexDirection: 'column',
          }}>
            {/* Header row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <h3 style={{ margin: 0, fontSize: '16px' }}>Edit Country Borders</h3>
              <button
                onClick={() => {
                  setIsMapFullscreen(fs => {
                    // resize after the state flip so Mapbox sees the new dimensions
                    setTimeout(() => countryMap?.resize(), 80);
                    return !fs;
                  });
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '5px',
                  padding: '4px 10px', borderRadius: '6px', cursor: 'pointer',
                  border: '1px solid #ccc', background: '#f5f5f5',
                  fontSize: '12px', fontWeight: 600, color: '#333',
                }}
                title={isMapFullscreen ? 'Exit fullscreen' : 'Edit in fullscreen'}
              >
                {isMapFullscreen ? '⛶ Exit Fullscreen' : '⛶ Fullscreen'}
              </button>
            </div>

            {/* Map container */}
            <div style={{ position: 'relative', flex: '1 1 auto', minHeight: isMapFullscreen ? 0 : '400px', maxHeight: isMapFullscreen ? undefined : '450px' }}>
              {(originalGeoJson === undefined || !mapContainer || !countryMap) && (
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: '#f0f0f0', borderRadius: '4px', zIndex: 1,
                }}>
                  <div>
                    {originalGeoJson === undefined && <div>Loading country data...</div>}
                    {originalGeoJson !== undefined && !mapContainer && <div>Initializing map container...</div>}
                    {originalGeoJson !== undefined && mapContainer && !countryMap && <div>Loading map...</div>}
                  </div>
                </div>
              )}
              <div
                ref={(el) => { if (el) setMapContainer(el); }}
                style={{
                  width: '100%', height: '100%',
                  minHeight: isMapFullscreen ? '0' : '400px',
                  borderRadius: '4px', overflow: 'hidden',
                  border: '1px solid #ddd', backgroundColor: '#f0f0f0',
                }}
              />
              {originalGeoJson === null && countryMap && (
                <div style={{
                  position: 'absolute', top: '10px', left: '10px', right: '10px',
                  padding: '12px', backgroundColor: '#fff3cd',
                  border: '1px solid #ffc107', borderRadius: '4px', zIndex: 2, fontSize: '14px',
                }}>
                  <strong>Note:</strong> Country borders not found in GeoJSON file. You can draw new borders using the polygon tool.
                </div>
              )}
            </div>

            {/* Submit proposal row */}
            <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
              <button
                className="primary-button"
                onClick={handleSubmitProposal}
                disabled={!hasChanges || isSubmittingProposal}
              >
                {isSubmittingProposal ? 'Submitting…' : 'Submit Border Proposal'}
              </button>
              <button
                onClick={() => borderVectorInputRef.current?.click()}
                disabled={isSubmittingProposal}
                title="Import a border from a GeoJSON, KML, GPX, or zipped Shapefile"
                style={{
                  padding: '6px 12px', borderRadius: '6px', cursor: 'pointer',
                  border: '1px solid #ccc', background: '#f5f5f5',
                  fontSize: '13px', fontWeight: 600, color: '#333',
                }}
              >
                📂 Import border
              </button>
              <input
                ref={borderVectorInputRef}
                type="file"
                accept={VECTOR_IMPORT_ACCEPT}
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleImportBorderVector(file);
                  e.target.value = '';
                }}
              />
              {hasChanges && !isSubmittingProposal && (
                <span style={{ fontSize: '12px', color: '#666' }}>You have unsaved changes</span>
              )}
              {proposalError && (
                <span style={{ fontSize: '12px', color: '#d32f2f' }}>{proposalError}</span>
              )}
              {saveError && (
                <span style={{ fontSize: '12px', color: '#d32f2f' }}>{saveError}</span>
              )}
              <span style={{ fontSize: '11px', color: '#999', marginLeft: 'auto' }}>
                Use the draw tools to edit borders, then submit for admin approval.
              </span>
            </div>

            {/* Proposal status panel */}
            {lastProposal && (
              <div style={{
                marginTop: '10px',
                padding: '12px 14px',
                border: '1px solid #d0d7de',
                borderRadius: '6px',
                backgroundColor: lastProposal.status === 'approved' ? '#f0fdf4'
                  : lastProposal.status === 'rejected' ? '#fff5f5'
                  : '#fffbea',
                flexShrink: 0,
              }}>
                {/* Header row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '13px', fontWeight: 700 }}>
                    Border Proposal
                  </span>
                  <span style={{
                    fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '10px',
                    backgroundColor:
                      lastProposal.status === 'approved' ? '#22c55e'
                      : lastProposal.status === 'rejected' ? '#ef4444'
                      : '#f59e0b',
                    color: '#fff',
                    textTransform: 'uppercase' as const,
                    letterSpacing: '0.04em',
                  }}>
                    {lastProposal.status}
                  </span>
                </div>

                {/* Proposal metadata */}
                <div style={{ fontSize: '11px', color: '#555', marginBottom: '8px', lineHeight: '1.6' }}>
                  <span style={{ fontFamily: 'monospace' }}>ID: {lastProposal.id.slice(0, 8)}…{lastProposal.id.slice(-4)}</span>
                  <span style={{ marginLeft: '12px' }}>
                    {new Date(lastProposal.createdAt).toLocaleString()}
                  </span>
                </div>

                {/* Diff preview (Phase 7) */}
                {lastProposal.proposedFeatureSnapshot && (
                  <div style={{
                    fontSize: '11px', color: '#444',
                    backgroundColor: '#f6f8fa', borderRadius: '4px',
                    padding: '7px 10px', marginBottom: '8px',
                    fontFamily: 'monospace', lineHeight: '1.8',
                  }}>
                    {proposalDiff ? (
                      <>
                        {/* Vertices */}
                        <div>
                          <span style={{ color: '#888' }}>Vertices: </span>
                          <span style={{ color: '#e67e22' }}>{proposalDiff.vertices?.original ?? '?'}</span>
                          {' → '}
                          <span style={{ color: '#27ae60' }}>{proposalDiff.vertices?.proposed ?? '?'}</span>
                          {proposalDiff.vertices?.delta !== undefined && (
                            <span style={{
                              marginLeft: '6px',
                              color: proposalDiff.vertices.delta > 0 ? '#16a34a' : proposalDiff.vertices.delta < 0 ? '#dc2626' : '#888',
                            }}>
                              ({proposalDiff.vertices.delta > 0 ? '+' : ''}{proposalDiff.vertices.delta})
                            </span>
                          )}
                        </div>
                        {/* Area */}
                        {proposalDiff.area?.approxOriginalKm2 !== undefined && (
                          <div>
                            <span style={{ color: '#888' }}>Area:     </span>
                            <span>{proposalDiff.area.approxOriginalKm2.toLocaleString()} km²</span>
                            {' → '}
                            <span>{proposalDiff.area.approxProposedKm2.toLocaleString()} km²</span>
                            {proposalDiff.area.deltaPct !== null && (
                              <span style={{
                                marginLeft: '6px',
                                color: proposalDiff.area.deltaPct > 0 ? '#16a34a' : proposalDiff.area.deltaPct < 0 ? '#dc2626' : '#888',
                              }}>
                                ({proposalDiff.area.deltaPct > 0 ? '+' : ''}{proposalDiff.area.deltaPct}%)
                              </span>
                            )}
                          </div>
                        )}
                        {/* Centroid shift */}
                        {proposalDiff.centroid?.approxShiftKm !== undefined && (
                          <div>
                            <span style={{ color: '#888' }}>Centroid: </span>
                            shifted ~{proposalDiff.centroid.approxShiftKm} km
                          </div>
                        )}
                        {/* Geometry type change warning */}
                        {proposalDiff.geometryTypeChanged && (
                          <div style={{ color: '#b45309', marginTop: '2px' }}>
                            ⚠ Geometry type changed
                          </div>
                        )}
                      </>
                    ) : (
                      /* Fallback: basic vertex count while diff is loading */
                      <>
                        {lastProposal.originalFeatureSnapshot && (
                          <div>
                            <span style={{ color: '#888' }}>Before: </span>
                            {lastProposal.originalFeatureSnapshot.geometry?.type ?? '—'}{' '}
                            <span style={{ color: '#e67e22' }}>
                              ({countVertices(lastProposal.originalFeatureSnapshot.geometry)} vertices)
                            </span>
                          </div>
                        )}
                        <div>
                          <span style={{ color: '#888' }}>After:  </span>
                          {lastProposal.proposedFeatureSnapshot.geometry?.type ?? '—'}{' '}
                          <span style={{ color: '#27ae60' }}>
                            ({countVertices(lastProposal.proposedFeatureSnapshot.geometry)} vertices)
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Approve / Reject buttons — admin only, only while submitted */}
                {(isMainAdmin || isCountryAdmin) && lastProposal.status === 'submitted' && (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleApproveProposal(lastProposal.id)}
                      disabled={isApprovingProposal || isRejectingProposal}
                      style={{
                        padding: '5px 14px', fontSize: '12px', fontWeight: 600,
                        backgroundColor: isApprovingProposal ? '#ccc' : '#16a34a',
                        color: '#fff', border: 'none', borderRadius: '4px',
                        cursor: isApprovingProposal ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {isApprovingProposal ? 'Approving…' : '✓ Approve'}
                    </button>
                    <button
                      onClick={() => handleRejectProposal(lastProposal.id)}
                      disabled={isApprovingProposal || isRejectingProposal}
                      style={{
                        padding: '5px 14px', fontSize: '12px', fontWeight: 600,
                        backgroundColor: isRejectingProposal ? '#ccc' : '#dc2626',
                        color: '#fff', border: 'none', borderRadius: '4px',
                        cursor: isRejectingProposal ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {isRejectingProposal ? 'Rejecting…' : '✕ Reject'}
                    </button>
                    <span style={{ fontSize: '11px', color: '#888', alignSelf: 'center' }}>
                      {isMainAdmin ? 'You can approve any country.' : 'You can approve your country.'}
                    </span>
                  </div>
                )}

                {lastProposal.status === 'approved' && (
                  <div style={{ fontSize: '12px', color: '#16a34a', fontWeight: 600 }}>
                    ✓ Border changes applied to the live map.
                    {lastProposal.approvedBy && (
                      <span style={{ fontWeight: 400, color: '#555', marginLeft: '6px' }}>
                        Approved by {lastProposal.approvedBy.slice(0, 8)}…
                      </span>
                    )}
                    {lastProposal.rewardTxHash && (
                      <div style={{ fontWeight: 400, color: '#555', marginTop: '4px', fontSize: '11px' }}>
                        Reward: {lastProposal.rewardAmount} {lastProposal.rewardCurrency ?? 'USDC'}
                        {' '}·{' '}
                        <span style={{ fontFamily: 'monospace' }}>
                          tx {lastProposal.rewardTxHash.slice(0, 10)}…
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {lastProposal.status === 'rejected' && (
                  <div style={{ fontSize: '12px', color: '#dc2626' }}>
                    ✕ Proposal rejected.
                    {lastProposal.rejectionReason && (
                      <span style={{ color: '#555', marginLeft: '6px' }}>Reason: {lastProposal.rejectionReason}</span>
                    )}
                  </div>
                )}

                {/* ── On-Chain Receipt & Reward Escrow Panel ─────────────────── */}
                {(isMainAdmin || isCountryAdmin) &&
                 ['approved', 'rewardFunded', 'paid'].includes(lastProposal.status) &&
                 onChainAvailable !== false && (
                  <div style={{
                    marginTop: '12px',
                    padding: '10px 12px',
                    backgroundColor: '#f0f9ff',
                    borderRadius: '6px',
                    border: '1px solid #bae6fd',
                    fontSize: '12px',
                  }}>
                    {/* Header */}
                    <div style={{ fontWeight: 700, color: '#0369a1', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' as const }}>
                      <span>◆ On-Chain Receipt</span>
                      {onChainAvailable === null && (
                        <span style={{ fontWeight: 400, color: '#888', fontSize: '11px' }}>checking…</span>
                      )}
                      {_REWARDS_CONTRACT_ID && (
                        <a
                          href={_explorerContract(_REWARDS_CONTRACT_ID)!}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: '10px', fontWeight: 400, color: '#0369a1', textDecoration: 'none', marginLeft: 'auto', backgroundColor: '#e0f2fe', padding: '1px 7px', borderRadius: '9999px' }}
                        >
                          View contract ↗
                        </a>
                      )}
                    </div>

                    {/* Receipt status */}
                    {onChainReceipt ? (
                      <div style={{ marginBottom: '8px' }}>
                        <span style={{ color: '#16a34a', fontWeight: 600 }}>✓ Anchored</span>
                        {' · '}
                        <span style={{ color: '#555' }}>
                          Status: <strong>{String(onChainReceipt.status ?? '').replace('_', ' ')}</strong>
                        </span>
                        {lastProposal.onChainSubmitHash && (
                          <div style={{ marginTop: '2px', fontSize: '11px' }}>
                            tx:{' '}
                            <a
                              href={_explorerTx(lastProposal.onChainSubmitHash)!}
                              target="_blank" rel="noopener noreferrer"
                              style={{ color: '#0369a1', fontFamily: 'monospace', textDecoration: 'none' }}
                              title={lastProposal.onChainSubmitHash}
                            >
                              {lastProposal.onChainSubmitHash.slice(0, 10)}… ↗
                            </a>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ marginBottom: '8px', color: '#6b7280' }}>
                        Not anchored on-chain yet.
                        <button
                          onClick={handleAnchorOnChain}
                          disabled={isAnchoringOnChain}
                          style={{
                            marginLeft: '10px',
                            padding: '3px 10px',
                            fontSize: '11px',
                            fontWeight: 600,
                            backgroundColor: isAnchoringOnChain ? '#e5e7eb' : '#0369a1',
                            color: isAnchoringOnChain ? '#888' : '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: isAnchoringOnChain ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {isAnchoringOnChain ? 'Anchoring…' : '⚓ Anchor on-chain'}
                        </button>
                      </div>
                    )}

                    {/* Escrow section — only when receipt is anchored */}
                    {onChainReceipt && (
                      <>
                        <div style={{ borderTop: '1px solid #bae6fd', paddingTop: '8px', marginTop: '4px', fontWeight: 600, color: '#0369a1', marginBottom: '6px' }}>
                          Reward Escrow
                        </div>

                        {onChainEscrow && !onChainEscrow.released && !onChainEscrow.refunded ? (
                          /* Escrow is funded and pending settlement */
                          <div>
                            <div style={{ color: '#555', marginBottom: '6px' }}>
                              <span style={{ color: '#16a34a' }}>● Funded</span>
                              {' · Amount: '}
                              <strong>{onChainEscrow.amount}</strong>
                              {lastProposal.onChainFundHash && (
                                <span style={{ marginLeft: '6px' }}>
                                  <a
                                    href={_explorerTx(lastProposal.onChainFundHash)!}
                                    target="_blank" rel="noopener noreferrer"
                                    style={{ color: '#0369a1', fontFamily: 'monospace', fontSize: '11px', textDecoration: 'none' }}
                                    title={lastProposal.onChainFundHash}
                                  >
                                    tx {lastProposal.onChainFundHash.slice(0, 10)}… ↗
                                  </a>
                                </span>
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                onClick={handleReleaseReward}
                                disabled={isReleasingReward || isRefundingReward}
                                style={{
                                  padding: '4px 12px', fontSize: '11px', fontWeight: 600,
                                  backgroundColor: isReleasingReward ? '#e5e7eb' : '#16a34a',
                                  color: isReleasingReward ? '#888' : '#fff',
                                  border: 'none', borderRadius: '4px',
                                  cursor: isReleasingReward ? 'not-allowed' : 'pointer',
                                }}
                              >
                                {isReleasingReward ? 'Releasing…' : '▶ Release to Agent'}
                              </button>
                              <button
                                onClick={handleRefundReward}
                                disabled={isReleasingReward || isRefundingReward}
                                style={{
                                  padding: '4px 12px', fontSize: '11px', fontWeight: 600,
                                  backgroundColor: isRefundingReward ? '#e5e7eb' : '#dc2626',
                                  color: isRefundingReward ? '#888' : '#fff',
                                  border: 'none', borderRadius: '4px',
                                  cursor: isRefundingReward ? 'not-allowed' : 'pointer',
                                }}
                              >
                                {isRefundingReward ? 'Refunding…' : '↩ Refund to Treasury'}
                              </button>
                            </div>
                          </div>
                        ) : onChainEscrow?.released ? (
                          <div style={{ color: '#16a34a' }}>
                            ✓ Reward released to agent.
                            {lastProposal.onChainReleaseHash && (
                              <span style={{ marginLeft: '6px' }}>
                                <a
                                  href={_explorerTx(lastProposal.onChainReleaseHash)!}
                                  target="_blank" rel="noopener noreferrer"
                                  style={{ color: '#0369a1', fontFamily: 'monospace', fontSize: '11px', textDecoration: 'none' }}
                                  title={lastProposal.onChainReleaseHash}
                                >
                                  tx {lastProposal.onChainReleaseHash.slice(0, 10)}… ↗
                                </a>
                              </span>
                            )}
                          </div>
                        ) : onChainEscrow?.refunded ? (
                          <div style={{ color: '#6b7280' }}>↩ Escrow refunded to treasury.</div>
                        ) : (
                          /* No escrow yet — show fund form */
                          <div>
                            <div style={{ color: '#6b7280', marginBottom: '6px' }}>
                              No reward escrow yet. Fund it to pay the agent on-chain.
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '6px' }}>
                              <label style={{ color: '#374151', fontSize: '11px' }}>
                                Token contract address (C… address):
                              </label>
                              <input
                                type="text"
                                value={fundTokenAddr}
                                onChange={e => setFundTokenAddr(e.target.value)}
                                placeholder="CDLZFC3SYJYDZT7K67VZ75HPJVIEUVNIXF47ZG2FB2RMQQVU2HHGCYSC"
                                style={{
                                  padding: '4px 8px', fontSize: '11px', borderRadius: '4px',
                                  border: '1px solid #bae6fd', width: '100%', boxSizing: 'border-box' as const,
                                  fontFamily: 'monospace',
                                }}
                              />
                              <label style={{ color: '#374151', fontSize: '11px', marginTop: '4px' }}>
                                Amount (base units — 10 000 000 = 1 USDC):
                              </label>
                              <input
                                type="number"
                                value={fundAmountStr}
                                onChange={e => setFundAmountStr(e.target.value)}
                                placeholder="10000000"
                                min="1"
                                style={{
                                  padding: '4px 8px', fontSize: '11px', borderRadius: '4px',
                                  border: '1px solid #bae6fd', width: '160px',
                                }}
                              />
                            </div>
                            <button
                              onClick={handleFundReward}
                              disabled={isFundingReward || !fundTokenAddr.trim() || !fundAmountStr.trim()}
                              style={{
                                padding: '4px 14px', fontSize: '11px', fontWeight: 600,
                                backgroundColor: isFundingReward || !fundTokenAddr.trim() || !fundAmountStr.trim()
                                  ? '#e5e7eb' : '#0369a1',
                                color: isFundingReward || !fundTokenAddr.trim() || !fundAmountStr.trim()
                                  ? '#888' : '#fff',
                                border: 'none', borderRadius: '4px',
                                cursor: isFundingReward ? 'not-allowed' : 'pointer',
                              }}
                            >
                              {isFundingReward ? 'Funding…' : '💰 Fund Escrow'}
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {!canEditBorders && (
          <div style={{ padding: '12px', backgroundColor: '#fff3cd', borderRadius: '4px' }}>
            Only the main admin or country admin can edit country borders.
          </div>
        )}
        </div>
      </div>
      
      {/* KYC Verification Modal */}
      {showKYCVerificationModal && kycSearchAddress && countryIso2 && (
        <KYCVerificationModal
          isOpen={showKYCVerificationModal}
          onClose={() => {
            setShowKYCVerificationModal(false);
            // Reload KYC data after verification
            if (kycSearchAddress) {
              handleKYCSearch();
            }
          }}
          userAddress={kycSearchAddress}
          countryCode={countryIso2}
          countryName={countryName}
          contractClient={contractClient}
          readOnlyClient={new ReadOnlyContractClient()}
          onKYCVerified={async () => {
            // Reload KYC data after verification
            if (kycSearchAddress) {
              await handleKYCSearch();
            }
          }}
        />
      )}
    </div>
  );
};
